import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Clock, Award, Shield, FileText, Users, ArrowLeft, Sparkles, PlayCircle, RotateCcw, MonitorSmartphone, BookOpen, ChevronDown, Building2, Laptop, Tablet, Smartphone, Download, Printer, Check, Target, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
const atAGlance = [{
  icon: Users,
  title: 'Target Audience',
  desc: 'Food handlers at all levels, including new and experienced employees working with food.'
}, {
  icon: MonitorSmartphone,
  title: 'Training Format',
  desc: '100% online, self-paced learning with engaging video modules and knowledge checks.'
}, {
  icon: FileText,
  title: 'Assessment',
  desc: 'Multiple-choice exam at the end of the course. A 70% pass mark is required.'
}, {
  icon: RotateCcw,
  title: 'Exam Retakes',
  desc: "Unlimited free exam retakes included if you don't pass on your first attempt."
}, {
  icon: Award,
  title: 'Certification',
  desc: 'Receive an instant CPD-accredited certificate upon completion, valid for 3 years.'
}, {
  icon: Shield,
  title: 'Compliance',
  desc: 'Fully aligned with Irish FSAI guidelines and legislation. Accepted Nationwide.'
}];
const courseAims = ['Provide complete HACCP Level 1 & 2 food safety training', 'Develop understanding of HACCP principles', 'Improve hazard control and monitoring', 'Reduce food contamination risks', 'Support food safety management', 'Improve safe food handling practices', 'Support workplace food safety compliance'];
const learningObjectives = ['Understand food safety hazards', 'Identify food contamination risks', 'Apply safe food handling practices', 'Understand HACCP principles', 'Control food safety hazards', 'Follow monitoring procedures', 'Maintain food safety records', 'Follow food safety procedures', 'Understand food safety responsibilities', 'Apply HACCP in the workplace'];
const howItWorks = [{
  title: 'Instant Access',
  desc: 'Start your HACCP Level 1 & 2 training immediately after enrolment.'
}, {
  title: 'Learn at Your Pace',
  desc: 'Watch video-based food safety training modules at your own speed.'
}, {
  title: 'Pass the Exam',
  desc: 'Take the final exam when ready. 70% pass mark. Unlimited attempts at no extra cost.'
}, {
  title: 'Get Certified',
  desc: 'Download your CPD-accredited HACCP Level 1 & 2 certificate instantly. Valid for 3 years.'
}];
const devices = [{
  icon: Laptop,
  title: 'Desktop',
  features: ['Large screen optimised', 'Keyboard shortcuts', 'Multi-tab support', 'Full browser experience']
}, {
  icon: Tablet,
  title: 'Tablet',
  features: ['Responsive layout', 'Gesture navigation', 'Portrait & landscape', 'Touch-optimised interface']
}, {
  icon: Smartphone,
  title: 'Mobile',
  features: ['Responsive layout', 'Gesture navigation', 'Portrait & landscape', 'Dedicated iOS & Android app']
}];
const courseModules = [{
  title: 'Introduction',
  duration: '~10 mins',
  desc: 'An overview of the course structure, food safety in Ireland, and what you will learn throughout the HACCP Level 1 & 2 programme.'
}, {
  title: 'Level 1 — Stage 1',
  duration: '~25 mins',
  desc: 'Covers essential food safety awareness required before handling food, including personal hygiene, safe food handling practices, and understanding contamination risks.'
}, {
  title: 'Level 1 — Stage 2',
  duration: '~30 mins',
  desc: 'Builds on Stage 1 with additional food safety skills expected within the first month, including maintaining hygiene standards, following safe procedures, and understanding responsibilities.'
}, {
  title: 'Level 2',
  duration: '~40 mins',
  desc: 'Introduces HACCP principles, hazard analysis, critical control points, monitoring procedures, contamination prevention, record keeping, and food safety responsibilities.'
}, {
  title: 'Level 2 Exam',
  duration: '~15 mins',
  desc: 'A multiple-choice assessment covering all course content. 70% pass mark required. Unlimited free retakes included.'
}];
const faqs: {
  q: string;
  a: string;
}[] = [{
  q: 'What is HACCP Level 1 & 2 training?',
  a: 'It is a comprehensive food safety course combining induction-level skills (Level 1) with the additional hazard control and HACCP principles (Level 2) required for established food handlers in Ireland.'
}, {
  q: 'Who should complete HACCP Level 1 & 2 training?',
  a: 'All food handlers, including new employees and experienced staff working in restaurants, cafes, catering businesses, retail food premises, and food production environments.'
}, {
  q: 'What is the difference between HACCP Level 1 and Level 2?',
  a: 'Level 1 covers essential induction training for the first 3 months of employment. Level 2 covers advanced HACCP principles, hazard analysis, and monitoring required for staff working beyond 3 months.'
}, {
  q: 'Is HACCP Level 1 & 2 training required?',
  a: 'Yes. Employers are legally required to ensure staff receive appropriate food safety training aligned with FSAI guidelines and EU Regulation (EC) No 852/2004.'
}, {
  q: 'When should HACCP Level 1 & 2 training be completed?',
  a: 'Level 1 Stage 1 should be completed before handling food, Stage 2 within 1 month. Level 2 within 3–6 months for high-risk environments, or 6–12 months for low-risk environments.'
}, {
  q: 'Is this HACCP Level 1 & 2 training fully online?',
  a: 'Yes. The course, assessment, and certification are 100% online. No classroom attendance is required.'
}, {
  q: 'How long does HACCP Level 1 & 2 training take?',
  a: 'The course takes approximately 120 minutes to complete, but it is entirely self-paced so you can take breaks and return at any time.'
}, {
  q: 'Will I receive a HACCP Level 1 & 2 certificate?',
  a: 'Yes. You will receive an instant CPD-accredited digital certificate upon passing the final assessment. An optional hard copy can also be ordered.'
}, {
  q: 'Is this HACCP Level 1 & 2 training CPD accredited?',
  a: 'Yes. The course provides 4 CPD credits and the certificate includes full CPD accreditation details (Activity #1015939).'
}, {
  q: 'Can I complete HACCP Level 1 & 2 together?',
  a: 'Yes. This course seamlessly combines both levels into a single, comprehensive programme that you complete in one enrolment.'
}, {
  q: 'Do I need to complete Level 1 before Level 2?',
  a: 'The course is structured to guide you through Level 1 foundational knowledge before progressing to Level 2 principles automatically. No separate enrolment needed.'
}, {
  q: 'Can employers enrol multiple staff members?',
  a: 'Yes. We offer a Group Booking option with a dedicated manager dashboard to assign training, track completion, and download certificates for your team.'
}, {
  q: 'Are unlimited exam retakes included?',
  a: 'Yes. You can retake the final assessment as many times as needed at no extra cost until you achieve the 70% pass mark.'
}, {
  q: 'How often should HACCP Level 1 & 2 training be renewed?',
  a: 'The certification is valid for 3 years, after which refresher training is recommended to stay current with food safety regulations.'
}, {
  q: 'Is this suitable for new food businesses?',
  a: 'Absolutely. It is the ideal foundational compliance training to ensure your new team meets all FSAI requirements from day one.'
}];
function Accordion({
  items,
  type
}: {
  items: {
    title: string;
    content: React.ReactNode;
    meta?: string;
  }[];
  type: 'module' | 'faq';
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(type === 'module' ? 0 : null);
  return <div className="space-y-3" data-id="element-577">
      {items.map((item, i) => <div key={i} className="bg-white border-2 border-forest-100 rounded-2xl overflow-hidden" data-id="element-578">
          <button onClick={() => setOpenIndex(openIndex === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-forest-50/50 transition-colors" data-id="element-579">
            <div className="flex items-center" data-id="element-580">
              {type === 'module' && <PlayCircle className="w-5 h-5 text-ember-500 mr-3 shrink-0" data-id="element-581" />}
              <span className="font-bold text-forest-900" data-id="element-582">{item.title}</span>
            </div>
            <div className="flex items-center" data-id="element-583">
              {item.meta && <span className="text-sm text-gray-400 mr-4 hidden sm:block" data-id="element-584">
                  {item.meta}
                </span>}
              <ChevronDown className={`w-5 h-5 text-forest-500 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} data-id="element-585" />
            </div>
          </button>
          <AnimatePresence data-id="element-586">
            {openIndex === i && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-587">
                <div className="px-6 py-4 bg-forest-50/30 border-t border-forest-100 text-sm text-gray-600 leading-relaxed" data-id="element-588">
                  {item.content}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>)}
    </div>;
}
export function HaccpLevel12Page() {
  return <div className="min-h-screen bg-cream-50" data-id="element-589">
      {/* Breadcrumb */}
      <div className="bg-white border-b-2 border-forest-100" data-id="element-590">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5" data-id="element-591">
          <Link to="/courses" className="inline-flex items-center text-sm font-bold text-forest-700 hover:text-ember-600 transition-colors group" data-id="element-592">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-593" />{' '}
            Back to all courses
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 text-white overflow-hidden" data-id="element-594">
        <div className="absolute inset-0 bg-mesh opacity-30" data-id="element-595"></div>
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-ember-500/15 rounded-full blur-3xl" data-id="element-596"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-forest-400/10 rounded-full blur-3xl" data-id="element-597"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-32" data-id="element-598">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-599">
            <Badge variant="gradient" className="mb-6" data-id="element-600">
              <Sparkles className="w-3 h-3 mr-1" data-id="element-601" /> ONLINE COURSE
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-display font-bold leading-[1.05] mb-6" data-id="element-602">
              HACCP Level 1 & 2<br data-id="element-603" />
              <span className="text-ember-400" data-id="element-604">Training Online</span>
            </h1>
            <p className="text-lg md:text-xl text-forest-100 leading-relaxed mb-10 max-w-2xl" data-id="element-605">
              Complete food safety training covering both induction-level food
              safety skills and the additional HACCP knowledge expected of
              employees working with food. Get certified instantly.
            </p>
            <div className="flex flex-wrap gap-4" data-id="element-606">
              {[{
              icon: Clock,
              label: '~120 Minutes'
            }, {
              icon: Award,
              label: 'CPD Accredited'
            }, {
              icon: FileText,
              label: 'Instant Certificate'
            }, {
              icon: RotateCcw,
              label: 'Unlimited Retakes'
            }].map(stat => <div key={stat.label} className="flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2.5 border border-white/10" data-id="element-607">
                  <stat.icon className="w-4 h-4 text-ember-400 mr-2" data-id="element-608" />
                  <span className="text-sm font-semibold" data-id="element-609">{stat.label}</span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* MAIN LAYOUT: Content + Sticky Sidebar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20 -mt-20 pb-24" data-id="element-610">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-id="element-611">
          {/* LEFT COLUMN */}
          <div className="lg:col-span-8 space-y-16" data-id="element-612">
            {/* COURSE OVERVIEW */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl border-2 border-forest-50" data-id="element-613">
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-6" data-id="element-614">
                Course Overview
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-lg" data-id="element-615">
                <p data-id="element-616">
                  This HACCP Level 1 & 2 training course combines both
                  induction-level food safety training and the additional food
                  safety skills expected of employees working with food beyond
                  the first three months.
                </p>
                <p data-id="element-617">
                  The course begins with{' '}
                  <strong className="text-forest-800" data-id="element-618">HACCP Level 1</strong>{' '}
                  training which covers essential food safety knowledge for new
                  food handlers. It then progresses to{' '}
                  <strong className="text-forest-800" data-id="element-619">HACCP Level 2</strong>{' '}
                  training which introduces HACCP principles, hazard control,
                  contamination prevention, monitoring procedures, and food
                  safety responsibilities.
                </p>
                <p data-id="element-620">
                  Suitable for new and experienced food handlers working in{' '}
                  <strong className="text-forest-800" data-id="element-621">
                    restaurants, cafes, catering businesses, retail food
                    premises, and food production environments
                  </strong>
                  .
                </p>
                <p data-id="element-622">
                  Learners complete structured video modules followed by a
                  multiple-choice assessment. Upon successful completion, a
                  HACCP Level 1 & 2 certificate is issued instantly.
                </p>
              </div>
            </motion.section>

            {/* AT A GLANCE */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-623">
              <Badge variant="outline" className="mb-4" data-id="element-624">
                Quick Facts
              </Badge>
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-8" data-id="element-625">
                At a Glance
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-626">
                {atAGlance.map((item, i) => <motion.div key={i} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.08
              }} className="bg-white p-6 rounded-2xl border-2 border-forest-100 hover:border-ember-300 transition-colors" data-id="element-627">
                    <div className="w-12 h-12 bg-gradient-to-br from-forest-50 to-cream-100 rounded-xl flex items-center justify-center mb-4 border border-forest-100" data-id="element-628">
                      <item.icon className="w-6 h-6 text-forest-600" data-id="element-629" />
                    </div>
                    <h3 className="font-display font-bold text-forest-900 mb-1" data-id="element-630">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed" data-id="element-631">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.section>

            {/* LEGISLATION & FSAI GUIDELINES */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-632">
              <div className="bg-gradient-to-br from-forest-900 to-forest-950 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden" data-id="element-633">
                <div className="absolute top-0 right-0 w-72 h-72 bg-ember-500/10 rounded-full blur-3xl" data-id="element-634"></div>
                <div className="relative z-10" data-id="element-635">
                  <Badge variant="warning" className="mb-6" data-id="element-636">
                    Irish Legislation
                  </Badge>
                  <h2 className="text-3xl font-display font-bold mb-4" data-id="element-637">
                    Built for Food Safety Compliance
                  </h2>
                  <p className="text-forest-100 text-lg leading-relaxed mb-10" data-id="element-638">
                    Employers must ensure staff receive appropriate food safety
                    training and understand HACCP principles. This course
                    provides structured instruction covering safe food handling,
                    contamination prevention, monitoring procedures, and food
                    safety responsibilities.
                  </p>

                  <h3 className="text-2xl font-display font-bold mb-8 text-ember-400" data-id="element-639">
                    FSAI Training Guidelines
                  </h3>

                  <div className="space-y-6" data-id="element-640">
                    {/* Level 1 */}
                    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10" data-id="element-641">
                      <div className="flex items-center mb-4" data-id="element-642">
                        <div className="w-10 h-10 bg-ember-500 rounded-xl flex items-center justify-center text-white font-display font-bold mr-3" data-id="element-643">
                          1
                        </div>
                        <h4 className="font-display font-bold text-xl" data-id="element-644">
                          Level 1 — Induction Training
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-13" data-id="element-645">
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-646">
                          <p className="text-ember-400 font-bold text-sm mb-1" data-id="element-647">
                            Stage 1 — Before handling food
                          </p>
                          <p className="text-forest-200 text-sm" data-id="element-648">
                            Basic food safety awareness, personal hygiene, safe
                            handling practices, and contamination risks.
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-649">
                          <p className="text-ember-400 font-bold text-sm mb-1" data-id="element-650">
                            Stage 2 — Within 1 month
                          </p>
                          <p className="text-forest-200 text-sm" data-id="element-651">
                            Maintaining hygiene standards, following safe food
                            procedures, and understanding responsibilities.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Level 2 */}
                    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10" data-id="element-652">
                      <div className="flex items-center mb-4" data-id="element-653">
                        <div className="w-10 h-10 bg-forest-500 rounded-xl flex items-center justify-center text-white font-display font-bold mr-3" data-id="element-654">
                          2
                        </div>
                        <h4 className="font-display font-bold text-xl" data-id="element-655">
                          Level 2 — Established Employees
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-13" data-id="element-656">
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-657">
                          <p className="text-ember-400 font-bold text-sm mb-1" data-id="element-658">
                            3–6 months
                          </p>
                          <p className="text-forest-200 text-sm" data-id="element-659">
                            Expected timeframe for employees in high-risk food
                            environments.
                          </p>
                        </div>
                        <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-id="element-660">
                          <p className="text-ember-400 font-bold text-sm mb-1" data-id="element-661">
                            6–12 months
                          </p>
                          <p className="text-forest-200 text-sm" data-id="element-662">
                            Expected timeframe for employees in lower-risk food
                            environments.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-forest-200 text-sm mt-8 leading-relaxed" data-id="element-663">
                    This course explains food safety hazards — biological,
                    chemical, and physical — and how to control them using HACCP
                    principles. It covers safe food handling, contamination
                    prevention, personal hygiene, monitoring, record keeping,
                    and food safety responsibilities.
                  </p>
                </div>
              </div>
            </motion.section>

            {/* WHAT THIS COURSE COVERS */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-664">
              <Badge variant="outline" className="mb-4" data-id="element-665">
                Content
              </Badge>
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-8" data-id="element-666">
                What This Course Covers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-667">
                <div className="bg-white p-8 rounded-3xl border-2 border-forest-100 shadow-lg" data-id="element-668">
                  <h3 className="text-xl font-display font-bold text-forest-900 mb-6 flex items-center" data-id="element-669">
                    <Target className="w-6 h-6 mr-2 text-ember-500" data-id="element-670" /> Course
                    Aims
                  </h3>
                  <ul className="space-y-3" data-id="element-671">
                    {courseAims.map((aim, i) => <li key={i} className="flex items-start" data-id="element-672">
                        <CheckCircle2 className="w-5 h-5 text-forest-500 mr-3 shrink-0 mt-0.5" data-id="element-673" />
                        <span className="text-gray-700 text-sm leading-relaxed" data-id="element-674">
                          {aim}
                        </span>
                      </li>)}
                  </ul>
                </div>
                <div className="bg-white p-8 rounded-3xl border-2 border-forest-100 shadow-lg" data-id="element-675">
                  <h3 className="text-xl font-display font-bold text-forest-900 mb-6 flex items-center" data-id="element-676">
                    <BookOpen className="w-6 h-6 mr-2 text-ember-500" data-id="element-677" />{' '}
                    Learning Objectives
                  </h3>
                  <ul className="space-y-3" data-id="element-678">
                    {learningObjectives.map((obj, i) => <li key={i} className="flex items-start" data-id="element-679">
                        <CheckCircle2 className="w-5 h-5 text-forest-500 mr-3 shrink-0 mt-0.5" data-id="element-680" />
                        <span className="text-gray-700 text-sm leading-relaxed" data-id="element-681">
                          {obj}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.section>

            {/* HOW IT WORKS */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-682">
              <Badge variant="outline" className="mb-4" data-id="element-683">
                Simple Process
              </Badge>
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-8" data-id="element-684">
                How It Works
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-685">
                {howItWorks.map((step, i) => <motion.div key={i} initial={{
                opacity: 0,
                y: 15
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: i * 0.1
              }} className="relative bg-white p-7 rounded-2xl border-2 border-forest-100 hover:border-forest-300 transition-colors overflow-hidden" data-id="element-686">
                    <span className="absolute top-3 right-4 text-6xl font-display font-bold text-forest-50 select-none" data-id="element-687">
                      {i + 1}
                    </span>
                    <div className="relative z-10" data-id="element-688">
                      <h3 className="font-display font-bold text-forest-900 text-lg mb-2" data-id="element-689">
                        {step.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed" data-id="element-690">
                        {step.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
              <p className="text-sm text-gray-500 mt-6 leading-relaxed" data-id="element-691">
                This training is delivered entirely online and can be completed
                at any time. Accessible on desktop, tablet, and mobile devices.
              </p>
            </motion.section>

            {/* MULTI-DEVICE */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-692">
              <div className="bg-gradient-to-br from-cream-100 to-white rounded-3xl p-8 md:p-12 border-2 border-forest-100 shadow-xl" data-id="element-693">
                <div className="text-center mb-10" data-id="element-694">
                  <h2 className="text-3xl font-display font-bold text-forest-900 mb-3" data-id="element-695">
                    One Platform, Every Device
                  </h2>
                  <p className="text-gray-600 text-lg" data-id="element-696">
                    Start on your laptop, continue on your tablet, finish on
                    your phone. Progress syncs automatically.
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-id="element-697">
                  {devices.map((device, i) => <div key={i} className="bg-white p-6 rounded-2xl border border-forest-100 text-center group hover:border-ember-300 hover:shadow-lg transition-all" data-id="element-698">
                      <div className="w-16 h-16 mx-auto bg-forest-50 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-ember-50 transition-colors" data-id="element-699">
                        <device.icon className="w-8 h-8 text-forest-700 group-hover:text-ember-500 transition-colors" data-id="element-700" />
                      </div>
                      <h3 className="font-display font-bold text-forest-900 mb-4" data-id="element-701">
                        {device.title}
                      </h3>
                      <ul className="space-y-2 text-sm text-gray-600 text-left" data-id="element-702">
                        {device.features.map((f, idx) => <li key={idx} className="flex items-center" data-id="element-703">
                            <Check className="w-4 h-4 text-forest-500 mr-2 shrink-0" data-id="element-704" />{' '}
                            {f}
                          </li>)}
                      </ul>
                    </div>)}
                </div>
                <div className="bg-forest-50 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 border border-forest-100" data-id="element-705">
                  <div data-id="element-706">
                    <p className="font-bold text-forest-900 text-sm" data-id="element-707">
                      Prerequisites
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-708">
                      Good understanding of English. Stable internet connection.
                      Closed Captions included for all video content.
                    </p>
                  </div>
                  <Badge variant="success" className="shrink-0" data-id="element-709">
                    Fully Accessible
                  </Badge>
                </div>
              </div>
            </motion.section>

            {/* CERTIFICATION */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-710">
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-4" data-id="element-711">
                Certification
              </h2>
              <p className="text-gray-600 mb-6" data-id="element-712">
                Awarded instantly upon achieving the required 70% pass mark.
                Your certificate serves as proof of compliance for workplace
                audits and regulatory inspections.
              </p>
              <div className="flex flex-wrap gap-3 mb-8" data-id="element-713">
                {[{
                label: 'Accreditation',
                value: 'CPD'
              }, {
                label: 'Credits',
                value: '4'
              }, {
                label: 'Validity',
                value: '3 Years'
              }, {
                label: 'Activity #',
                value: '1015939'
              }].map(stat => <div key={stat.label} className="bg-white px-5 py-3 rounded-xl border-2 border-forest-100" data-id="element-714">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-715">
                      {stat.label}
                    </p>
                    <p className="font-display font-bold text-forest-900" data-id="element-716">
                      {stat.value}
                    </p>
                  </div>)}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-717">
                <div className="bg-white p-8 rounded-3xl border-2 border-forest-500 shadow-xl relative overflow-hidden" data-id="element-718">
                  <div className="absolute top-0 right-0 bg-forest-500 text-white px-4 py-1.5 rounded-bl-xl text-xs font-bold" data-id="element-719">
                    INCLUDED
                  </div>
                  <Download className="w-10 h-10 text-forest-600 mb-4" data-id="element-720" />
                  <h3 className="text-xl font-display font-bold text-forest-900 mb-1" data-id="element-721">
                    Digital Certificate
                  </h3>
                  <p className="text-3xl font-display font-bold text-forest-700 mb-4" data-id="element-722">
                    FREE
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600" data-id="element-723">
                    {['Instant Download', 'PDF Format', 'Print-Ready', 'Shareable'].map(f => <li key={f} className="flex items-center" data-id="element-724">
                        <Check className="w-4 h-4 text-forest-500 mr-2" data-id="element-725" /> {f}
                      </li>)}
                  </ul>
                </div>
                <div className="bg-white p-8 rounded-3xl border-2 border-forest-100 shadow-md" data-id="element-726">
                  <Printer className="w-10 h-10 text-gray-400 mb-4" data-id="element-727" />
                  <h3 className="text-xl font-display font-bold text-forest-900 mb-1" data-id="element-728">
                    Hard Copy Certificate
                  </h3>
                  <p className="text-3xl font-display font-bold text-gray-500 mb-4" data-id="element-729">
                    Optional
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600" data-id="element-730">
                    {['Premium Card Stock', 'Posted to Your Address', 'Ideal for Framing', 'Workplace Display'].map(f => <li key={f} className="flex items-center" data-id="element-731">
                        <Check className="w-4 h-4 text-forest-500 mr-2" data-id="element-732" /> {f}
                      </li>)}
                  </ul>
                </div>
              </div>
            </motion.section>

            {/* GROUP TRAINING */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-733">
              <div className="bg-gradient-to-br from-forest-900 to-forest-950 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden" data-id="element-734">
                <div className="absolute inset-0 bg-mesh opacity-20" data-id="element-735"></div>
                <div className="relative z-10" data-id="element-736">
                  <Badge variant="gradient" className="mb-6" data-id="element-737">
                    <Building2 className="w-3 h-3 mr-1" data-id="element-738" /> Group Booking
                  </Badge>
                  <h2 className="text-3xl font-display font-bold mb-4" data-id="element-739">
                    Training for Teams
                  </h2>
                  <p className="text-forest-200 text-lg mb-8 max-w-2xl" data-id="element-740">
                    Enrol multiple staff members. Managers can assign training,
                    track completion, and download certificates from a dedicated
                    dashboard. Bulk licence purchasing available.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8" data-id="element-741">
                    {[{
                    title: 'Easy Team Management',
                    desc: 'Add individually or bulk import via CSV. Assign courses with one click.'
                  }, {
                    title: 'Manager Dashboard',
                    desc: 'Monitor group completion rates, individual progress, and certificate status.'
                  }, {
                    title: 'Real-Time Tracking',
                    desc: 'Download certificates for team members and generate compliance reports.'
                  }, {
                    title: 'Instant Certificates',
                    desc: 'Certificates generated immediately for each team member upon completion.'
                  }].map((f, i) => <div key={i} className="bg-white/5 backdrop-blur-sm p-5 rounded-2xl border border-white/10" data-id="element-742">
                        <h4 className="font-bold text-ember-400 mb-2 text-sm" data-id="element-743">
                          {f.title}
                        </h4>
                        <p className="text-sm text-forest-200" data-id="element-744">{f.desc}</p>
                      </div>)}
                  </div>
                  <div className="bg-gradient-to-r from-ember-500 to-ember-600 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-745">
                    <div data-id="element-746">
                      <h4 className="font-display font-bold text-xl mb-1" data-id="element-747">
                        Enterprise Solution
                      </h4>
                      <p className="text-sm text-ember-100" data-id="element-748">
                        White-labelled platform, unlimited users, SSO, custom
                        branding, and bespoke reporting.
                      </p>
                    </div>
                    <Button variant="outline" className="bg-white text-ember-600 border-white hover:bg-ember-50 shrink-0" data-id="element-749">
                      Learn More
                    </Button>
                  </div>
                </div>
              </div>
            </motion.section>

            {/* COURSE CONTENT ACCORDION */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-750">
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-8" data-id="element-751">
                Course Content
              </h2>
              <Accordion type="module" items={courseModules.map(m => ({
              title: m.title,
              meta: m.duration,
              content: <p data-id="element-753">{m.desc}</p>
            }))} data-id="element-752" />
            </motion.section>

            {/* FAQ */}
            <motion.section initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} data-id="element-754">
              <h2 className="text-3xl font-display font-bold text-forest-900 mb-8" data-id="element-755">
                Frequently Asked Questions
              </h2>
              <Accordion type="faq" items={faqs.map(f => ({
              title: f.q,
              content: <p data-id="element-757">{f.a}</p>
            }))} data-id="element-756" />
              <div className="mt-8 bg-cream-100 rounded-2xl p-6 text-center border border-forest-100" data-id="element-758">
                <p className="font-bold text-forest-900 mb-2" data-id="element-759">
                  Still have questions? We're here to help.
                </p>
                <Link to="/contact" className="text-ember-600 hover:text-ember-700 font-bold underline transition-colors" data-id="element-760">
                  Contact our support team →
                </Link>
              </div>
            </motion.section>
          </div>

          {/* STICKY SIDEBAR */}
          <div className="lg:col-span-4" data-id="element-761">
            <div className="sticky top-28" data-id="element-762">
              <div className="bg-white rounded-3xl shadow-2xl border-2 border-forest-100 overflow-hidden" data-id="element-763">
                <div className="bg-gradient-to-br from-forest-800 to-forest-900 p-8 text-white text-center relative overflow-hidden" data-id="element-764">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-ember-500/20 rounded-full blur-2xl" data-id="element-765"></div>
                  <p className="text-forest-300 text-sm font-bold uppercase tracking-wider mb-2 relative z-10" data-id="element-766">
                    Online Course Fee
                  </p>
                  <p className="text-6xl font-display font-bold relative z-10 mb-1" data-id="element-767">
                    €39
                  </p>
                  <p className="text-forest-300 text-xs relative z-10" data-id="element-768">
                    No hidden fees · Instant access
                  </p>
                </div>
                <div className="p-8 space-y-5" data-id="element-769">
                  <Button fullWidth size="lg" variant="primary" className="text-lg" data-id="element-770">
                    Enrol Now €39
                  </Button>
                  <Button fullWidth size="lg" variant="outline" data-id="element-771">
                    Group Booking
                  </Button>
                  <div className="pt-5 border-t-2 border-forest-50 space-y-3" data-id="element-772">
                    {['Instant course access', 'CPD Accredited Certificate', 'Unlimited free exam retakes', '3-year certificate validity'].map(item => <div key={item} className="flex items-center text-sm text-gray-600" data-id="element-773">
                        <CheckCircle2 className="w-4 h-4 text-forest-500 mr-3 shrink-0" data-id="element-774" />
                        <span data-id="element-775">{item}</span>
                      </div>)}
                  </div>
                  <div className="flex items-center text-sm text-gray-500 pt-3 border-t border-forest-50" data-id="element-776">
                    <Shield className="w-4 h-4 text-forest-500 mr-2 shrink-0" data-id="element-777" />
                    <span data-id="element-778">Secure payment via Stripe</span>
                  </div>
                </div>
              </div>

              <div className="mt-6 bg-gradient-to-br from-ember-50 to-forest-50 rounded-2xl p-6 border-2 border-ember-200" data-id="element-779">
                <h4 className="font-display font-bold text-forest-900 mb-2" data-id="element-780">
                  Need corporate training?
                </h4>
                <p className="text-sm text-gray-700 mb-4 leading-relaxed" data-id="element-781">
                  We deliver on-site at your premises or at our Dublin 12
                  training centre. Volume discounts available.
                </p>
                <Link to="/contact" className="text-sm font-bold text-forest-700 hover:text-ember-600 underline transition-colors" data-id="element-782">
                  Contact Corporate Team →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}